<script setup>
import { computed, onMounted, onUnmounted, nextTick, ref, watch } from "vue";
import { gsap } from "gsap";
import Header from "@components/Header.vue";
import MobileMenu from "@components/MobileMenu.vue";
import Landing from "@components/Landing.vue";
import ParallaxDepthField from "@components/ParallaxDepthField.vue";
import Loading from "@components/Loading.vue";
import Scene from "@components/Scene.vue";
import Modal from "@components/Modal.vue";
import AchievementToast from "@components/AchievementToast.vue";
import GetStarted from "@components/GetStarted.vue";
import VotingIntro from "@components/modals/VotingIntro.vue";
import VotingSuccess from "@components/modals/VotingSuccess.vue";
import SweepstakeIntro from "@components/modals/SweepstakeIntro.vue";
import Clip from "@components/modals/Clip.vue";
import LeaveTriviaConfirm from "@components/modals/LeaveTriviaConfirm.vue";
import ExploreIntro from "@components/modals/ExploreIntro.vue";
import { useGlobalStore } from "@stores/global";
import { useTriviaStore } from "@stores/trivia";

const CELL_SIZE_DESKTOP = 68;
const CELL_SIZE_MOBILE = 48;
const MOBILE_BREAKPOINT = 1024;
const STRIPE_COUNT = 0.05;
const STRIPE_INTERVAL = 3000;
const TRAIL_DURATION = 250;

const globalStore = useGlobalStore();
const triviaStore = useTriviaStore();

const grid = ref(null);
const cols = ref(1);
const rows = ref(1);
const cell_count = computed(() => cols.value * rows.value);

function updateCellSize() {
    const cellSize =
        window.innerWidth <= MOBILE_BREAKPOINT
            ? CELL_SIZE_MOBILE
            : CELL_SIZE_DESKTOP;
    cols.value = Math.round(window.innerWidth / cellSize);
    rows.value = Math.round(window.innerHeight / cellSize);

    if (!grid.value) return;

    grid.value.style.setProperty(
        "--cell-x",
        `${window.innerWidth / cols.value}px`,
    );
    grid.value.style.setProperty(
        "--cell-y",
        `${window.innerHeight / rows.value}px`,
    );
    grid.value.style.setProperty("--cols", cols.value);
    grid.value.style.setProperty("--rows", rows.value);

    randomizeStripedCells();
}

const stripedCells = ref(new Set());

function randomizeStripedCells() {
    const total = cell_count.value;
    const target = Math.floor(total * STRIPE_COUNT);
    const set = new Set();
    while (set.size < target) {
        set.add(Math.floor(Math.random() * total) + 1);
    }
    stripedCells.value = set;
}

const trailCells = ref(new Set());
const trailTimers = new Map();

function addTrailCell(index) {
    clearTimeout(trailTimers.get(index));

    const next = new Set(trailCells.value);
    next.add(index);
    trailCells.value = next;

    trailTimers.set(
        index,
        setTimeout(() => {
            const updated = new Set(trailCells.value);
            updated.delete(index);
            trailCells.value = updated;
            trailTimers.delete(index);
        }, TRAIL_DURATION),
    );
}

function handleMouseMove(event) {
    if (!grid.value) return;

    const rect = grid.value.getBoundingClientRect();
    const col = Math.floor(
        (event.clientX - rect.left) / (rect.width / cols.value),
    );
    const row = Math.floor(
        (event.clientY - rect.top) / (rect.height / rows.value),
    );

    if (col < 0 || col >= cols.value || row < 0 || row >= rows.value) return;

    addTrailCell(row * cols.value + col + 1);
}

let stripeInterval = null;

const cloudsLayer = ref(null);

const CLOUD_EXIT = [
    { x: 700, y: -700 },
    { x: 700, y: 700 },
    { x: -800, y: 600 },
];

function setCloudsState(landing, animated = true) {
    if (!cloudsLayer.value) return;
    const clouds = cloudsLayer.value.querySelectorAll(".cloud");
    if (!clouds.length) return;

    const reduce = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
    ).matches;

    clouds.forEach((cloud, i) => {
        const exit = CLOUD_EXIT[i] ?? CLOUD_EXIT[0];
        const target = landing
            ? { x: 0, y: 0, scale: 1, opacity: 0.1 }
            : { x: exit.x, y: exit.y, scale: 2.4, opacity: 0 };

        gsap.killTweensOf(cloud);
        if (!animated || reduce) {
            gsap.set(cloud, target);
            return;
        }
        gsap.to(cloud, {
            ...target,
            duration: landing ? 1.4 : 1.1,
            ease: landing ? "power2.out" : "power2.in",
        });
    });
}

watch(
    () => globalStore.isLanding,
    (landing) => setCloudsState(landing),
);

function playGridReveal() {
    if (!grid.value) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const cells = grid.value.querySelectorAll(".cell");
    if (!cells.length) return;

    gsap.killTweensOf(cells);
    gsap.from(cells, {
        opacity: 0,
        scale: 0.85,
        duration: 0.6,
        ease: "power2.out",
        stagger: {
            grid: [rows.value, cols.value],
            from: "start",
            amount: 0.7,
        },
    });
}

onMounted(async () => {
    updateCellSize();
    window.addEventListener("resize", updateCellSize);
    window.addEventListener("mousemove", handleMouseMove);
    stripeInterval = setInterval(randomizeStripedCells, STRIPE_INTERVAL);
    await nextTick();
    playGridReveal();
    setCloudsState(globalStore.isLanding, false);
});

onUnmounted(() => {
    window.removeEventListener("resize", updateCellSize);
    window.removeEventListener("mousemove", handleMouseMove);
    clearInterval(stripeInterval);
});
</script>

<template>
    <div class="grid" ref="grid">
        <div
            ref="cloudsLayer"
            class="clouds-layer pointer-events-none fixed inset-0"
        >
            <img
                class="cloud absolute -top-[200px] -right-[150px] h-[702px] w-[1052px] opacity-10"
                src="https://speed-cdn.expedia.com/images/elements/clouds.png"
                alt=""
            />
            <img
                class="cloud absolute right-0 bottom-0 h-[379px] w-[568px] opacity-10"
                src="https://speed-cdn.expedia.com/images/elements/clouds.png"
                alt=""
            />
            <img
                class="cloud absolute -bottom-[43px] -left-[279px] h-[515px] w-[772px] opacity-10"
                src="https://speed-cdn.expedia.com/images/elements/clouds.png"
                alt=""
            />
        </div>
        <Header v-if="!globalStore.isLanding" />
        <MobileMenu v-if="!globalStore.isLanding" />
        <template v-if="globalStore.isLanding">
            <ParallaxDepthField class="z-0" />
            <div
                class="pointer-events-none absolute inset-0 z-0 bg-[radial-gradient(circle_at_center,rgba(3,12,29,0.5)_0%,rgba(3,12,29,0.8)_70%)]"
            ></div>
        </template>
        <Scene
            :class="[
                'scene z-1 transition-opacity duration-500',
                triviaStore.gameState === 'results' ||
                globalStore.activePage === 'activities'
                    ? 'pointer-events-none opacity-0'
                    : 'opacity-100',
            ]"
        />
        <router-view v-if="!globalStore.isLanding" />
        <Transition name="landing-drift">
            <Landing
                v-if="globalStore.isLanding && !globalStore.pendingRoute"
            />
        </Transition>
        <Loading v-if="globalStore.pendingRoute && !globalStore.globeReady" />
        <GetStarted v-if="!globalStore.isLanding" />
        <AchievementToast />
        <Modal>
            <VotingIntro v-if="globalStore.modal.name === 'votingIntro'" />
            <VotingSuccess
                v-if="
                    globalStore.modal.name === 'votingSuccess' ||
                    globalStore.modal.name === 'sweepstakeSuccess'
                "
            />
            <SweepstakeIntro
                v-if="globalStore.modal.name === 'sweepstakeIntro'"
            />
            <Clip v-if="globalStore.modal.name === 'clip'" />
            <LeaveTriviaConfirm
                v-if="globalStore.modal.name === 'leaveTriviaConfirm'"
            />
        </Modal>
        <ExploreIntro />
        <div
            class="cell bg-marine-800 border-marine-500 relative isolate -z-1 border-r border-b"
            :class="{ active: stripedCells.has(i), hovered: trailCells.has(i) }"
            v-for="i in cell_count"
            :key="i"
            :style="{
                gridColumn: ((i - 1) % cols) + 1,
                gridRow: Math.floor((i - 1) / cols) + 1,
            }"
        ></div>
    </div>
</template>

<style scoped>
.grid {
    position: relative;
    isolation: isolate;
    display: grid;
    grid-template-columns: repeat(var(--cols), var(--cell-x));
    grid-template-rows: repeat(var(--rows), var(--cell-y));
    width: 100%;
    height: 100svh;
    overflow: hidden;
}

.scene {
    grid-column: 1 / -1;
    grid-row: 2 / -1;
}

.cell {
    transition: background-color 0.8s ease-out;
}

.cell.hovered {
    transition: background-color 0.1s ease-out;
}

.cell.hovered {
    background-color: var(--color-marine-700);
}

.cell::before {
    content: "";
    position: absolute;
    inset: 0;
    background-image: repeating-linear-gradient(
        135deg,
        transparent 0,
        transparent 5px,
        var(--color-marine-500) 5px,
        var(--color-marine-500) 6px
    );
    opacity: 0;
    transition: opacity 0.5s ease-in-out;
    pointer-events: none;
}

.cell.active::before {
    opacity: 1;
}

.cell.hovered::before {
    opacity: 0;
}
</style>
